import axios from "axios";
import React, { useEffect, useState } from "react";

import {
  getDate,
  formatDate,
  formatQuantity,
  getPRAge,
  getPRStatus,
  findLinkedPO,
  getRemainingPRQuantity,
} from "../../utils/reportUtils";
import { useLocation, useNavigate } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL;

const Requisitions = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [prs, setPrs] = useState([]);
  const [pos, setPos] = useState([]);

  const [q, setQ] = useState("");
  const location = useLocation();
  const navigate = useNavigate();
  const dashboardStatus = location.state?.preset;
  const [preset, setPreset] = useState(dashboardStatus || "");
  const storedUser = JSON.parse(localStorage.getItem("user"));

  const authHeader = {
    headers: {
      Authorization: `Bearer ${storedUser?.accessToken}`,
    },
  };

  const [sort, setSort] = useState({
    key: "age",
    direction: 1,
  });

  // India date, not UTC, so "today" is correct between 00:00 and 05:30 IST.
  const today = getDate(new Date());

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);

    try {
      const [p, o] = await Promise.all([
        axios.get(`${API}/open-pr/latest`, authHeader),
        axios.get(`${API}/open-po/latest`, authHeader),
      ]);

      setPrs(p.data || []);
      setPos(o.data || []);
    } catch (e) {
      setError(
        e.response?.data?.Exception ||
          e.response?.data?.message ||
          "Could not load requisitions.",
      );
    } finally {
      setLoading(false);
    }
  };

  const getStatusClass = (value) => {
    return value === "CLOSED" ? "b-grey" : "b-info";
  };

  const getStatusText = (value) => {
    return value === "CLOSED" ? "Closed" : "Open";
  };

  const getAgeClass = (age) => {
    if (age > 7) {
      return "b-short";
    }

    if (age > 5) {
      return "b-risk";
    }

    return "";
  };

  const getLinkedPO = (pr) => {
    const directPO = String(pr.purchaseOrder || "").trim();
    if (directPO) {
      return { purchasingDocument: directPO };
    }

    return findLinkedPO(pr, pos);
  };

  const changeSort = (key) => {
    if (sort.key === key) {
      setSort({
        key: key,
        direction: -sort.direction,
      });
    } else {
      setSort({
        key: key,
        direction: 1,
      });
    }
  };

  const getSortIcon = (key) => {
    if (sort.key !== key) {
      return null;
    }

    return <span className="ar">{sort.direction > 0 ? "↑" : "↓"}</span>;
  };

  const rows = prs
    .filter((pr) => {
      if (preset === "OPEN") {
        return getPRStatus(pr, pos) === "OPEN";
      }

      if (preset === "CLOSED") {
        return getPRStatus(pr, pos) === "CLOSED";
      }

      return true;
    })
    .filter((pr) => {
      const search = q.trim().toLowerCase();

      if (!search) {
        return true;
      }

      const value = `
        ${pr.purchaseRequisition || ""}
        ${pr.itemCode || ""}
        ${pr.material || ""}
        ${pr.shortText || ""}
      `.toLowerCase();

      return value.includes(search);
    })
    .sort((a, b) => {
      let first = "";
      let second = "";

      if (sort.key === "pr") {
        first = a.purchaseRequisition || "";
        second = b.purchaseRequisition || "";
      }

      if (sort.key === "qty") {
        first = getRemainingPRQuantity(a);
        second = getRemainingPRQuantity(b);
      }

      if (sort.key === "date") {
        first = a.requisitionDate || "";
        second = b.requisitionDate || "";
      }

      if (sort.key === "status") {
        first = getPRStatus(a, pos);
        second = getPRStatus(b, pos);
      }

      if (sort.key === "age" || sort.key === "urgency") {
        first = getPRAge(a, today);
        second = getPRAge(b, today);
      }

      if (first < second) {
        return -sort.direction;
      }

      if (first > second) {
        return sort.direction;
      }

      return 0;
    });

  const exportCsv = () => {
    const header = [
      "PR Number",
      "Item Code",
      "Material Code",
      "Material Description",
      "PR Qty",
      "PR Creation Date",
      "Status",
      "Age",
      "PO Number",
    ];

    const lines = rows.map((pr) => {
      const linkedPO = getLinkedPO(pr);

      return [
        pr.purchaseRequisition,
        pr.itemCode,
        pr.material,
        pr.shortText,
        getRemainingPRQuantity(pr),
        formatDate(pr.requisitionDate),
        getPRStatus(pr, pos),
        getPRAge(pr, today),
        linkedPO?.purchasingDocument || "",
      ]
        .map((value) => `"${String(value ?? "").replace(/"/g, '""')}"`)
        .join(",");
    });

    const csv = [header.join(","), ...lines].join("\n");

    const blob = new Blob([csv], {
      type: "text/csv",
    });

    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");

    link.href = url;
    link.download = "purchase-requisitions.csv";
    link.click();

    URL.revokeObjectURL(url);
  };

  const presetButton = (value, label) => {
    return (
      <button
        className={`btn btn-o btn-sm${preset === value ? " on" : ""}`}
        onClick={() => setPreset(value)}
      >
        {label}
      </button>
    );
  };

  if (loading) {
    return (
      <div className="page">
        <div className="empty">Loading requisitions...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page">
        <div className="note err">
          <b>Couldn't load data.</b> {error}
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="ph">
        <div>
          <h1>Purchase requisitions</h1>

          <p>
            <strong>{rows.length}</strong> of {prs.length} PR lines · age is
            days since the PR was raised
          </p>
        </div>

        <button className="btn btn-o" onClick={exportCsv}>
          Export to Excel
        </button>
      </div>

      <div className="ctl">
        <div className="search">
          <input
            placeholder="Search PR number, item code, material or description..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>

        {presetButton("", "All")}

        {presetButton("OPEN", "Open PRs")}

        {presetButton("CLOSED", "Closed PRs")}
      </div>

      <div className="card">
        <div className="tw">
          <table className="dt">
            <thead>
              <tr>
                <th className="srt" onClick={() => changeSort("pr")}>
                  PR Number
                  {getSortIcon("pr")}
                </th>

                <th>Item Code</th>

                <th>Material Code</th>

                <th>Material Description</th>

                <th className="srt r" onClick={() => changeSort("qty")}>
                  PR Qty
                  {getSortIcon("qty")}
                </th>

                <th className="srt" onClick={() => changeSort("date")}>
                  PR Creation Date
                  {getSortIcon("date")}
                </th>

                <th className="srt" onClick={() => changeSort("status")}>
                  Status
                  {getSortIcon("status")}
                </th>

                <th className="srt r" onClick={() => changeSort("age")}>
                  Age
                  {getSortIcon("age")}
                </th>

                <th>PO Number</th>
              </tr>
            </thead>

            <tbody>
              {rows.length > 0 ? (
                rows.map((pr) => {
                  const age = getPRAge(pr, today);
                  const prStatus = getPRStatus(pr, pos);
                  const linkedPO = getLinkedPO(pr);
                  let ageClass = getAgeClass(age);

                  return (
                    <tr
                      key={pr.openPrId}
                      onClick={() => navigate(`../shortages/${pr.material}`)}
                      className="clk"
                    >
                      <td className="mono">{pr.purchaseRequisition || "—"}</td>

                      <td className="mono">{pr.itemCode || "—"}</td>

                      <td className="mono">{pr.material || "—"}</td>

                      <td>{pr.shortText || "—"}</td>

                      <td className="mono">
                        {formatQuantity(getRemainingPRQuantity(pr))}
                      </td>

                      <td>{formatDate(pr.requisitionDate)}</td>

                      <td>
                        <span className={`bg ${getStatusClass(prStatus)}`}>
                          {getStatusText(prStatus)}
                        </span>
                      </td>

                      <td>
                        {ageClass ? (
                          <span className={`bg ${ageClass}`}>{age} days</span>
                        ) : (
                          <span className="dim">{age} days</span>
                        )}
                      </td>

                      <td className="mono">
                        {linkedPO ? (
                          linkedPO.purchasingDocument || "Converted"
                        ) : (
                          <span className="bg b-out">No PO</span>
                        )}
                      </td>

                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={9} className="empty">
                    <strong>No PR's found.</strong>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Requisitions;
